import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  useMapEvents,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// ---------- Config ----------
const PROVIDER = (
  process.env.REACT_APP_GEOCODER_PROVIDER ||
  process.env.VITE_GEOCODER_PROVIDER ||
  "photon"
).toLowerCase(); // "mapbox" | "nominatim" | "photon"
const MAPBOX_TOKEN =
  process.env.REACT_APP_MAPBOX_TOKEN || process.env.VITE_MAPBOX_TOKEN;
const USE_MAPBOX = PROVIDER === "mapbox" && !!MAPBOX_TOKEN;
const USE_PHOTON = PROVIDER === "photon";
const DEFAULT_COUNTRIES = ["FR", "BE", "LU", "CH", "ES", "SE", "GB", "IE"];

// ---------- Helpers ----------
const debounce = (fn, ms = 250) => {
  let t;
  return (...a) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...a), ms);
  };
};
const abortable = () => {
  let c = new AbortController();
  return {
    next() {
      c.abort();
      c = new AbortController();
      return c.signal;
    },
  };
};
const fetchGuard = abortable();

const defaultIcon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 48],
});

const formatLabel = ({ number, street, postalCode, city, country }) => {
  const parts = [];
  const ns = `${number ? number + " " : ""}${street || ""}`.trim();
  if (ns) parts.push(ns);
  const pcCity = [postalCode, city].filter(Boolean).join(" ");
  if (pcCity) parts.push(pcCity);
  if (country) parts.push(country);
  return parts.join(", ");
};

// ---------- Providers ----------
const parseMapbox = (f) => {
  const ctx = (f.context || []).reduce((acc, c) => {
    const [k] = c.id.split(".");
    acc[k] = c.text;
    if (k === "postcode") acc.postcode = c.text;
    if (k === "place" || k === "locality") acc.city = c.text;
    if (k === "country") acc.country = c.text;
    return acc;
  }, {});
  const number = f.address || "";
  const street = f.text || "";
  const city = ctx.city || f.place_name?.split(",")?.[1]?.trim() || "";
  const postalCode = ctx.postcode || "";
  const country = ctx.country || "";
  return {
    id: f.id,
    label: f.place_name,
    labelShort: formatLabel({ number, street, postalCode, city, country }),
    lat: f.center?.[1],
    lng: f.center?.[0],
    number,
    street,
    city,
    postalCode,
    country,
  };
};

const parseNominatim = (it) => {
  const a = it.address || {};
  const number = a.house_number || "";
  const city = a.city || a.town || a.village || a.municipality || "";
  const street =
    a.road ||
    a.pedestrian ||
    a.footway ||
    a.path ||
    a.hamlet ||
    a.locality ||
    a.place ||
    a.neighbourhood ||
    a.suburb ||
    "";
  const postalCode = a.postcode || "";
  const country = a.country || "";
  return {
    id: it.place_id,
    label: it.display_name,
    labelShort: formatLabel({ number, street, postalCode, city, country }),
    lat: Number(it.lat),
    lng: Number(it.lon),
    number,
    street,
    city,
    postalCode,
    country,
  };
};

const PHOTON_PLACE_TYPES = new Set(["locality", "district", "hamlet"]);

const photonStreet = (p) => {
  if (p.street) return p.street;
  if (p.type === "street" && p.name) return p.name;
  if (PHOTON_PLACE_TYPES.has(p.type) || PHOTON_PLACE_TYPES.has(p.osm_value)) {
    return p.name || "";
  }
  return p.name || "";
};

const buildPhotonLabel = (p) => {
  const street = photonStreet(p);
  const number = p.housenumber || "";
  const city = p.city || "";
  const postalCode = p.postcode || "";
  const country = p.country || "";
  return formatLabel({ number, street, postalCode, city, country });
};

const parsePhoton = (f) => {
  const p = f.properties || {};
  const coords = f.geometry?.coordinates || [];
  const lng = Number(coords[0]);
  const lat = Number(coords[1]);
  const number = p.housenumber || "";
  const street = photonStreet(p);
  const city = p.city || "";
  const postalCode = p.postcode || "";
  const country = p.country || "";
  const label = buildPhotonLabel(p);
  return {
    id: `photon-${p.osm_type || "x"}-${p.osm_id || label}`,
    label,
    labelShort: label,
    lat,
    lng,
    number,
    street,
    city,
    postalCode,
    country,
    countrycode: p.countrycode || "",
  };
};

const filterByCountries = (items, countryCodes) => {
  if (!countryCodes?.length) return items;
  const codes = new Set(countryCodes.map((c) => c.toUpperCase()));
  const filtered = items.filter((it) =>
    codes.has((it.countrycode || "").toUpperCase()),
  );
  return filtered.length ? filtered : items;
};

/** Variantes de requête pour lieux-dits, voies sans tronçon OSM, saisies avec virgules. */
const FRENCH_ADDRESS_PREFIX =
  /^(?:chemin|route|avenue|impasse|all[ée]e|place|boulevard|bd|ld|lieu\s*[-–]?\s*dit|zac|zae)\s+(?:de\s+(?:la\s+|l'|les\s+)?|du\s+|des\s+|d')?/i;

const extractLocalityFromFrenchAddress = (line) => {
  const trimmed = (line || "").trim();
  if (!trimmed) return null;

  const endMatch = trimmed.match(/,?\s*(\d{4,5})\s+(.+)$/);
  const postalCity = endMatch ? `${endMatch[1]} ${endMatch[2].trim()}` : "";
  const beforeCp = endMatch
    ? trimmed.slice(0, endMatch.index).replace(/,\s*$/, "").trim()
    : trimmed;
  const locality = beforeCp.replace(FRENCH_ADDRESS_PREFIX, "").trim();
  if (!locality || locality.toLowerCase() === beforeCp.toLowerCase())
    return null;
  return [locality, postalCity].filter(Boolean).join(" ");
};

const geocodeQueryVariants = (q) => {
  const trimmed = (q || "").trim();
  if (!trimmed) return [];

  const ordered = [];
  const seen = new Set();
  const add = (value) => {
    const v = (value || "").trim();
    const key = v.toLowerCase();
    if (!v || seen.has(key)) return;
    seen.add(key);
    ordered.push(v);
  };

  const localityQuery = extractLocalityFromFrenchAddress(trimmed);
  if (localityQuery) add(localityQuery);

  const noLieuDit = trimmed.replace(/^lieu\s*[-–]?\s*dit\s*,?\s*/i, "").trim();
  if (noLieuDit) add(noLieuDit);

  const normalized = trimmed
    .replace(/^lieu\s*[-–]?\s*dit\s*,?\s*/i, "")
    .replace(/,/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (normalized) add(normalized);

  const keywords = normalized
    .replace(/[''´`]/g, "")
    .replace(/\b\d{4,5}\b/g, "")
    .split(/\s+/)
    .filter((w) => w.length > 2);
  if (keywords.length >= 2) {
    add(keywords.slice(0, 5).join(" "));
  }

  add(trimmed);

  return ordered;
};

const normalizeSearchToken = (value) =>
  (value || "").toLowerCase().normalize("NFD").replace(/\p{M}/gu, "");

const resultsMatchQueryIntent = (results, originalQuery) => {
  const localityQuery = extractLocalityFromFrenchAddress(originalQuery);
  if (!localityQuery) return true;

  const keyToken = normalizeSearchToken(localityQuery.split(/\s+\d{4,5}/)[0])
    .split(/[^a-z0-9']+/)
    .filter((w) => w.length > 2)[0];
  if (!keyToken) return true;

  return results.some((r) => {
    const haystack = normalizeSearchToken(
      [r.label, r.labelShort, r.street, r.city, r.number].join(" "),
    );
    return haystack.includes(keyToken);
  });
};

const runSuggestProviders = async (query, countryCodes) => {
  const providers = [];
  if (USE_PHOTON) providers.push(suggestPhoton);
  if (USE_MAPBOX) providers.push(suggestMapbox);
  if (PROVIDER === "nominatim") providers.push(suggestNominatim);
  if (!USE_PHOTON) providers.push(suggestPhoton);
  if (PROVIDER !== "nominatim" && !USE_PHOTON && !USE_MAPBOX) {
    providers.push(suggestNominatim);
  }

  const seen = new Set();
  for (const provider of providers) {
    if (seen.has(provider)) continue;
    seen.add(provider);
    try {
      const results = await provider(query, countryCodes);
      if (results.length) return results;
    } catch (e) {
      if (e?.name === "AbortError") throw e;
    }
  }
  return [];
};

async function suggestMapbox(q, countryCodes) {
  if (!MAPBOX_TOKEN) throw new Error("MAPBOX_TOKEN manquant");
  const url = new URL(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json`,
  );
  url.searchParams.set("access_token", MAPBOX_TOKEN);
  url.searchParams.set("autocomplete", "true");
  url.searchParams.set("language", "fr");
  url.searchParams.set("limit", "5");
  if (countryCodes?.length)
    url.searchParams.set("country", countryCodes.join(","));
  const r = await fetch(url, { signal: fetchGuard.next() });
  const j = await r.json();
  return (j.features || []).map(parseMapbox);
}

async function suggestNominatim(q, countryCodes) {
  const url = new URL("https://nominatim.openstreetmap.org/search");
  url.searchParams.set("q", q);
  url.searchParams.set("format", "jsonv2");
  url.searchParams.set("addressdetails", "1");
  url.searchParams.set("limit", "5");
  url.searchParams.set("accept-language", "fr");
  if (countryCodes?.length)
    url.searchParams.set(
      "countrycodes",
      countryCodes.map((c) => c.toLowerCase()).join(","),
    );
  const r = await fetch(url.toString(), {
    mode: "cors",
    signal: fetchGuard.next(),
  });
  const j = await r.json();
  return j.map(parseNominatim);
}

async function suggestPhoton(q, countryCodes) {
  const url = new URL("https://photon.komoot.io/api/");
  url.searchParams.set("q", q);
  url.searchParams.set("limit", "5");
  url.searchParams.set("lang", "fr");
  const r = await fetch(url.toString(), {
    mode: "cors",
    signal: fetchGuard.next(),
  });
  const j = await r.json();
  const items = (j.features || []).map(parsePhoton);
  return filterByCountries(items, countryCodes);
}

async function reverseGeocodeMapbox(lat, lng) {
  if (!MAPBOX_TOKEN) throw new Error("MAPBOX_TOKEN manquant");
  const url = new URL(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json`,
  );
  url.searchParams.set("access_token", MAPBOX_TOKEN);
  url.searchParams.set("language", "fr");
  url.searchParams.set("limit", "1");
  url.searchParams.set("types", "address,place,locality");
  const r = await fetch(url, { signal: fetchGuard.next() });
  const j = await r.json();
  const f = (j.features || [])[0];
  return f ? parseMapbox(f) : null;
}

async function reverseGeocodeNominatim(lat, lng) {
  const url = new URL("https://nominatim.openstreetmap.org/reverse");
  url.searchParams.set("lat", String(lat));
  url.searchParams.set("lon", String(lng));
  url.searchParams.set("format", "jsonv2");
  url.searchParams.set("addressdetails", "1");
  url.searchParams.set("accept-language", "fr");
  const r = await fetch(url.toString(), {
    mode: "cors",
    signal: fetchGuard.next(),
  });
  const j = await r.json();
  if (!j) return null;
  return parseNominatim({
    place_id: j.place_id,
    display_name: j.display_name,
    address: j.address,
    lat: j.lat,
    lon: j.lon,
  });
}

async function reverseGeocodePhoton(lat, lng) {
  const url = new URL("https://photon.komoot.io/reverse");
  url.searchParams.set("lat", String(lat));
  url.searchParams.set("lon", String(lng));
  url.searchParams.set("lang", "fr");
  const r = await fetch(url.toString(), {
    mode: "cors",
    signal: fetchGuard.next(),
  });
  const j = await r.json();
  const f = (j.features || [])[0];
  return f ? parsePhoton(f) : null;
}

async function suggest(q, countryCodes = DEFAULT_COUNTRIES) {
  const variants = geocodeQueryVariants(q);
  let fallback = [];

  for (const query of variants) {
    const results = await runSuggestProviders(query, countryCodes);
    if (!results.length) continue;
    if (!fallback.length) fallback = results;
    if (resultsMatchQueryIntent(results, q)) return results;
  }

  return fallback;
}

async function reverseGeocode(lat, lng) {
  const providers = [];
  if (USE_PHOTON) providers.push(reverseGeocodePhoton);
  if (USE_MAPBOX) providers.push(reverseGeocodeMapbox);
  if (PROVIDER === "nominatim") providers.push(reverseGeocodeNominatim);
  if (!USE_PHOTON) providers.push(reverseGeocodePhoton);
  if (PROVIDER !== "nominatim" && !USE_PHOTON && !USE_MAPBOX) {
    providers.push(reverseGeocodeNominatim);
  }

  const seen = new Set();
  for (const provider of providers) {
    if (seen.has(provider)) continue;
    seen.add(provider);
    try {
      const result = await provider(lat, lng);
      if (result) return result;
    } catch (e) {
      if (e?.name === "AbortError") throw e;
    }
  }
  return null;
}

// ---------- Map bits ----------
function ClickToSetMarker({ onPick }) {
  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      onPick([lat, lng]); /* NO RECENTER HERE */
    },
  });
  return null;
}

function MapRecenter({ position, enabled }) {
  // only for programmatic changes (e.g. suggestion select)
  const map = useMap();
  useEffect(() => {
    if (!enabled) return;
    if (Array.isArray(position) && position.length === 2) {
      map.setView(position, map.getZoom(), { animate: true });
    }
  }, [position, enabled, map]);
  return null;
}

function CountryChips({ value, onChange }) {
  const { t } = useTranslation();
  const countries = [
    { code: "ALL", label: t("addressLocation.allCountries") },
    { code: "FR", label: "FR" },
    { code: "BE", label: "BE" },
    { code: "LU", label: "LU" },
    { code: "CH", label: "CH" },
    { code: "ES", label: "ES" },
    { code: "SE", label: "SE" },
    { code: "GB", label: "GB" },
    { code: "IE", label: "IE" },
  ];
  return (
    <div className="flex items-center gap-1">
      {countries.map((c) => {
        const active =
          value === "ALL" ? c.code === "ALL" : value.includes(c.code);
        return (
          <button
            key={c.code}
            type="button"
            onClick={() =>
              c.code === "ALL" ? onChange("ALL") : onChange([c.code])
            }
            className={`text-[11px] px-2.5 py-1 rounded-full border transition ${active ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-50"}`}
            title={c.label}
          >
            {c.label}
          </button>
        );
      })}
    </div>
  );
}

export function AddressGeocodeInput({
  onPick,
  variant = "default",
  value: controlledValue,
  onChange: controlledOnChange,
  placeholder,
  onBlur: onBlurProp,
  inputId,
  autoComplete = "street-address",
}) {
  const { t } = useTranslation();
  const isEmbedded = variant === "embedded";
  const isSiteField = variant === "site-field";
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState("");
  const value = isControlled ? controlledValue : internalValue;
  const setValue = isControlled ? controlledOnChange : setInternalValue;
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [countryFilter, setCountryFilter] = useState(DEFAULT_COUNTRIES);
  const [focused, setFocused] = useState(false);
  const suppressNextSuggestRef = useRef(false);
  const inputRef = useRef(null);
  const boxRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState(null);

  const updateMenuPosition = () => {
    if (!boxRef.current) return;
    const rect = boxRef.current.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  };

  const runSuggest = useMemo(
    () =>
      debounce(async (v, countries) => {
        if (suppressNextSuggestRef.current) {
          suppressNextSuggestRef.current = false;
          return;
        }
        const q = (v || "").trim();
        if (q.length < 2) {
          setItems([]);
          setOpen(false);
          return;
        }
        try {
          const cc = countries === "ALL" ? [] : countries;
          const res = await suggest(q, cc);
          setItems(res);
          if (focused) setOpen(true);
          setActive(0);
        } catch (e) {
          if (e?.name !== "AbortError") {
            setItems([]);
            if (focused) setOpen(true);
          }
        }
      }, 250),
    [focused],
  );

  useEffect(() => {
    runSuggest(value, countryFilter);
  }, [value, countryFilter, runSuggest]);
  useEffect(() => {
    const onDocClick = (e) => {
      const inBox = boxRef.current?.contains(e.target);
      const inMenu = e.target.closest?.("[data-address-geocode-menu]");
      if (!inBox && !inMenu) {
        setOpen(false);
        setFocused(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    if (!open || !isSiteField) {
      setMenuPosition(null);
      return undefined;
    }
    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [open, isSiteField, value]);

  const choose = (it) => {
    if (!isSiteField) {
      setValue(it.labelShort);
    }
    setOpen(false);
    setFocused(false);
    suppressNextSuggestRef.current = true;
    inputRef.current?.blur();
    onPick?.(it);
  };

  const handleBlur = () => {
    setFocused(false);
    onBlurProp?.(value);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      if (open && items.length > 0) {
        const selectedItem = items[active];
        if (selectedItem) {
          choose(selectedItem);
        }
      }
      return;
    }

    if (!open || !items.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      setFocused(false);
    }
  };

  const fieldPlaceholder =
    placeholder ??
    (isSiteField
      ? t("settingsSites.siteAddressPlaceholder")
      : t("addressLocation.searchPlaceholder"));

  const suggestionsList = !items.length ? (
    <div className="px-4 py-3 text-sm text-neutral-500">
      {t("addressLocation.noResults")}
    </div>
  ) : (
    items.map((it, idx) => (
      <button
        key={it.id}
        type="button"
        onMouseEnter={() => setActive(idx)}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => choose(it)}
        title={it.labelShort}
        className={`group block w-full text-left transition ${
          isSiteField
            ? `px-3 py-2.5 text-[14px] leading-[22px] ${idx === active ? "bg-[#f5f5f5]" : "bg-white"}`
            : `px-4 py-3 text-sm ${idx === active ? "bg-neutral-50" : "bg-white"}`
        }`}
      >
        <div className="flex items-start gap-2">
          <span
            className={`mt-0.5 inline-block shrink-0 rounded-full bg-neutral-300 ${
              isSiteField ? "h-1.5 w-1.5" : "h-2 w-2"
            }`}
          />
          <span
            className={
              isSiteField
                ? "font-normal text-[#222222]"
                : "font-medium text-neutral-900"
            }
          >
            {it.labelShort}
          </span>
        </div>
      </button>
    ))
  );

  const suggestionsPanel = open ? (
    <div
      className={
        isSiteField
          ? "w-full max-h-[220px] overflow-auto rounded border border-[#d3d3d3] bg-white shadow-md"
          : isEmbedded
            ? "max-h-[220px] overflow-auto rounded-lg border border-[#d3d3d3] bg-white shadow-md"
            : "absolute mt-2 w-full md:w-[560px] max-h-[320px] overflow-auto rounded-xl bg-white shadow-xl ring-1 ring-black/5"
      }
    >
      {suggestionsList}
    </div>
  ) : null;

  const portaledSuggestions =
    !isSiteField && open && menuPosition
      ? createPortal(
          <div
            data-address-geocode-menu
            className="fixed z-[2100] max-h-[220px] overflow-auto rounded-lg border border-[#d3d3d3] bg-white shadow-md"
            style={{
              top: menuPosition.top,
              left: menuPosition.left,
              width: menuPosition.width,
            }}
          >
            {suggestionsList}
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      ref={boxRef}
      className={`relative w-full ${isSiteField ? "z-[1]" : ""}`}
    >
      <div
        className={
          isSiteField
            ? "flex h-10 w-full items-center gap-2 rounded border border-[#d3d3d3] bg-white px-3"
            : isEmbedded
              ? "flex h-10 items-center gap-2 rounded-lg border border-[#d3d3d3] bg-white px-3 shadow-sm"
              : "flex items-center gap-2 rounded-full bg-white/95 backdrop-blur px-4 py-2 shadow-lg ring-1 ring-black/5"
        }
      >
        {!isSiteField && (
          <svg
            width={isEmbedded ? 16 : 18}
            height={isEmbedded ? 16 : 18}
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden
            className={`shrink-0 ${isEmbedded ? "text-[#888]" : ""}`}
          >
            <path
              d="M21 21l-4.35-4.35"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <circle
              cx="11"
              cy="11"
              r="7"
              stroke="currentColor"
              strokeWidth="1.5"
            />
          </svg>
        )}
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          className={
            isSiteField
              ? "min-w-0 flex-1 bg-transparent text-[14px] leading-[22px] text-[#222] outline-none placeholder:text-[#888]"
              : isEmbedded
                ? "min-w-0 flex-1 bg-transparent text-[14px] text-[#222] outline-none placeholder:text-[#888]"
                : "w-[min(72vw,600px)] md:w-[560px] bg-transparent outline-none text-sm placeholder:text-neutral-400"
          }
          placeholder={fieldPlaceholder}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onFocus={() => {
            setFocused(true);
            if ((value || "").trim().length >= 2)
              runSuggest(value, countryFilter);
            setOpen(true);
          }}
          onBlur={handleBlur}
          onKeyDown={onKeyDown}
          autoComplete={autoComplete}
        />
        {!isEmbedded && !isSiteField && (
          <div className="hidden md:block border-l pl-3 ml-1">
            <CountryChips
              value={
                Array.isArray(countryFilter) && countryFilter.length
                  ? countryFilter
                  : "ALL"
              }
              onChange={(val) => setCountryFilter(val)}
            />
          </div>
        )}
        {value && !isSiteField && (
          <button
            type="button"
            aria-label={t("addressLocation.clearSearchAria")}
            onClick={() => {
              setValue("");
              setItems([]);
              setOpen(false);
              setFocused(false);
              inputRef.current?.blur();
            }}
            className="rounded-full p-1 hover:bg-neutral-100"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
      </div>

      {isSiteField && open && (
        <div
          className="absolute left-0 right-0 top-full z-[10] mt-1"
          data-address-geocode-menu
        >
          {suggestionsPanel}
        </div>
      )}

      {!isSiteField && open && (
        <div
          className={
            isEmbedded
              ? "absolute left-0 right-0 top-full z-[1100] mt-1"
              : "absolute left-0 right-0 top-full z-[1100] mt-2 w-full md:w-[560px]"
          }
        >
          {suggestionsPanel}
        </div>
      )}
      {portaledSuggestions}
    </div>
  );
}

export { suggest };

// ---------- Main ----------
export default function FullScreenAddressMap({
  editFormData,
  setEditFormData,
  showSearch = true,
  mapHeight = "70vh",
  searchVariant = "default",
  /** Bandeaux adresse + coordonnées en bas de carte (désactivé sur création de site). */
  showMapOverlay = true,
  /** Incrémenter pour recentrer la carte après sélection depuis un champ externe. */
  recenterToken = 0,
}) {
  // normalized editFormData: { number, street, postalCode, city, country, location: [lat, lng] }
  const [shouldRecenter, setShouldRecenter] = useState(false);

  const position = useMemo(() => {
    const lat = Number(editFormData?.location?.[0]);
    const lng = Number(editFormData?.location?.[1]);
    return Number.isFinite(lat) && Number.isFinite(lng) && (lat || lng)
      ? [lat, lng]
      : [43.57074638611116, 1.4372704999999997];
  }, [editFormData?.location]);

  const applyAddress = (src) => {
    // Guarantee: if no house number, save as ""
    const number = src?.number ? String(src.number) : "";
    setEditFormData?.((prev) => ({
      ...prev,
      number,
      street: src?.street || prev?.street || "",
      city: src?.city || prev?.city || "",
      postalCode: src?.postalCode || prev?.postalCode || "",
      country: src?.country || prev?.country || "",
      location: [
        Number.isFinite(src?.lat) ? src.lat : prev?.location?.[0] || 0,
        Number.isFinite(src?.lng) ? src.lng : prev?.location?.[1] || 0,
      ],
    }));
  };

  const onPickSuggestion = (it) => {
    applyAddress(it);
    setShouldRecenter(true);
  };

  useEffect(() => {
    if (recenterToken > 0) setShouldRecenter(true);
  }, [recenterToken]);

  const setLocation = async ([lat, lng]) => {
    try {
      const rev = await reverseGeocode(Number(lat) || 0, Number(lng) || 0);
      if (rev) applyAddress({ ...rev, lat, lng });
      else
        setEditFormData?.((p) => ({ ...p, number: "", location: [lat, lng] }));
    } catch {
      setEditFormData?.((p) => ({ ...p, number: "", location: [lat, lng] }));
    }
    setShouldRecenter(false); // never recenter on click
  };

  return (
    <section className="relative w-full" style={{ height: mapHeight }}>
      {/* Search overlay */}
      {showSearch && (
        <div
          className={
            searchVariant === "embedded"
              ? "pointer-events-none absolute top-3 left-11 right-3 z-[1000]"
              : "pointer-events-none absolute top-4 left-1/2 z-[1000] -translate-x-1/2"
          }
        >
          <div className="pointer-events-auto">
            <AddressGeocodeInput
              onPick={onPickSuggestion}
              variant={searchVariant}
            />
          </div>
        </div>
      )}

      <MapContainer
        center={position}
        zoom={13}
        style={{ height: mapHeight, width: "100%" }}
        scrollWheelZoom
      >
        <TileLayer
          attribution="© OpenStreetMap"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <ClickToSetMarker onPick={setLocation} />
        <Marker position={position} icon={defaultIcon} />
        {/* Recenter only after programmatic changes (suggestion selection) */}
        <MapRecenter position={position} enabled={shouldRecenter} />
      </MapContainer>
      {showMapOverlay &&
        (editFormData?.street ||
          editFormData?.city ||
          editFormData?.postalCode ||
          editFormData?.number) && (
          <div className="absolute bottom-14 left-4 z-[1000] rounded-full bg-black/70 text-white text-xs px-3 py-1.5 shadow max-w-[70vw] overflow-hidden text-ellipsis whitespace-nowrap">
            {[editFormData?.number, editFormData?.street]
              .filter(Boolean)
              .join(" ") + (editFormData?.street ? ", " : "")}
            {[editFormData?.postalCode, editFormData?.city]
              .filter(Boolean)
              .join(" ")}
          </div>
        )}
      {showMapOverlay && (
        <div className="absolute bottom-4 left-4 z-[1000] rounded-full bg-black/70 text-white text-xs px-3 py-1.5 shadow">
          {position[0].toFixed(6)}, {position[1].toFixed(6)}
        </div>
      )}
    </section>
  );
}
